import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Divider, FormControlLabel, IconButton, MenuItem, Paper, Stack,
  TextField, Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageLayout, PageHeader, SectionCard, EmptyState, LoadingState } from '../../components/ui';
import {
  fetchSurvey, createSurvey, updateSurvey, validateSurvey, activateSurvey,
  createQuestion, updateQuestion, deleteQuestion, reorderQuestions,
  QUESTION_TYPES, CONDITION_OPERATORS, usesOptions, operatorNeedsValue,
} from '../../services/surveysApi';

const AUDIENCE_TYPES = [
  { value: 'all', label: 'Everyone' },
  { value: 'department', label: 'Department' },
  { value: 'role', label: 'Role' },
  { value: 'location', label: 'Location' },
  { value: 'employee', label: 'Specific employee (code)' },
];

const EMPTY_META = {
  title: '', description: '', is_anonymous: false, allow_multiple_submissions: false,
  start_date: '', end_date: '', audiences: [{ audience_type: 'all', audience_value: '' }],
};

function normalizeQuestion(q) {
  return {
    id: q.id,
    type: q.type,
    label: q.label || '',
    help_text: q.help_text || '',
    is_required: !!q.is_required,
    settings: q.settings || {},
    options: (q.options || []).map((o) => ({ value: o.value, label: o.label || o.value, is_exclusive: !!o.is_exclusive })),
    rule: q.rule
      ? { logic: q.rule.logic || 'AND', conditions: (q.rule.conditions || []).map((c) => ({ source_question_id: c.source_question_id, operator: c.operator, value: c.value ?? '' })) }
      : null,
  };
}

function toQuestionPayload(q) {
  return {
    type: q.type,
    label: q.label,
    help_text: q.help_text || null,
    is_required: !!q.is_required,
    settings: q.settings && Object.keys(q.settings).length ? q.settings : null,
    options: usesOptions(q.type)
      ? q.options.filter((o) => o.value !== '').map((o, i) => ({ value: o.value, label: o.label || o.value, sort_order: i, is_exclusive: !!o.is_exclusive }))
      : [],
    rule: q.rule && q.rule.conditions.length
      ? {
          logic: q.rule.logic,
          conditions: q.rule.conditions
            .filter((c) => c.source_question_id && c.operator)
            .map((c) => ({ source_question_id: Number(c.source_question_id), operator: c.operator, value: parseValue(c.operator, c.value) })),
        }
      : null,
  };
}

function parseValue(operator, value) {
  if (['in', 'not_in', 'between'].includes(operator)) {
    return String(value ?? '').split(',').map((s) => s.trim()).filter((s) => s !== '');
  }
  if (!operatorNeedsValue(operator)) return null;
  return value;
}

export default function SurveyBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = Boolean(id);

  const [meta, setMeta] = useState(EMPTY_META);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(editing);
  const [savingMeta, setSavingMeta] = useState(false);

  const loadSurvey = useCallback(async () => {
    const s = await fetchSurvey(id);
    setMeta({
      title: s.title || '',
      description: s.description || '',
      is_anonymous: !!s.is_anonymous,
      allow_multiple_submissions: !!s.allow_multiple_submissions,
      start_date: s.start_date ? s.start_date.slice(0, 10) : '',
      end_date: s.end_date ? s.end_date.slice(0, 10) : '',
      audiences: s.audiences?.length
        ? s.audiences.map((a) => ({ audience_type: a.audience_type, audience_value: a.audience_value || '' }))
        : [{ audience_type: 'all', audience_value: '' }],
    });
    setQuestions((s.questions || []).map(normalizeQuestion));
  }, [id]);

  useEffect(() => {
    if (!editing) return;
    loadSurvey().catch(() => toast.error('Failed to load survey.')).finally(() => setLoading(false));
  }, [editing, loadSurvey]);

  const setMetaField = (field, value) => setMeta((prev) => ({ ...prev, [field]: value }));

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      const payload = {
        ...meta,
        start_date: meta.start_date || null,
        end_date: meta.end_date || null,
        audiences: meta.audiences.filter((a) => a.audience_type),
      };
      if (editing) {
        await updateSurvey(id, payload);
        toast.success('Survey details saved.');
        loadSurvey();
      } else {
        const created = await createSurvey(payload);
        toast.success('Survey created. Now add your questions.');
        navigate(`/admin/surveys/${created.id}/edit`);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed.');
    } finally {
      setSavingMeta(false);
    }
  };

  const addQuestion = async () => {
    try {
      const created = await createQuestion(id, { type: 'text', label: 'Untitled question', is_required: false });
      setQuestions((prev) => [...prev, normalizeQuestion(created)]);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not add question.');
    }
  };

  const patchQuestion = (qid, patch) =>
    setQuestions((prev) => prev.map((q) => (q.id === qid ? { ...q, ...patch } : q)));

  const saveQuestion = async (q) => {
    try {
      const saved = await updateQuestion(id, q.id, toQuestionPayload(q));
      setQuestions((prev) => prev.map((x) => (x.id === q.id ? normalizeQuestion(saved) : x)));
      toast.success('Question saved.');
    } catch (e) {
      const firstError = e.response?.data?.errors && Object.values(e.response.data.errors)[0]?.[0];
      toast.error(firstError || e.response?.data?.message || 'Could not save question.');
    }
  };

  const removeQuestion = async (qid) => {
    try {
      await deleteQuestion(id, qid);
      setQuestions((prev) => prev.filter((x) => x.id !== qid));
    } catch {
      toast.error('Could not remove question.');
    }
  };

  const move = async (idx, dir) => {
    const next = [...questions];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setQuestions(next);
    try {
      await reorderQuestions(id, next.map((q, i) => ({ id: q.id, sort_order: i })));
    } catch {
      toast.error('Reorder failed.');
    }
  };

  const validateAndActivate = async () => {
    try {
      const v = await validateSurvey(id);
      if (!v.valid) {
        toast.error(`Cannot activate — ${v.errors?.[0] || 'invalid structure'}`);
        return;
      }
      await activateSurvey(id);
      toast.success('Survey activated.');
      navigate('/admin/surveys');
    } catch (e) {
      const errs = e.response?.data?.errors;
      toast.error((Array.isArray(errs) && errs[0]) || e.response?.data?.message || 'Activation failed.');
    }
  };

  if (loading) return <LoadingState />;

  return (
    <PageLayout>
      <PageHeader
        title={editing ? 'Edit survey' : 'New survey'}
        actions={<Button onClick={() => navigate('/admin/surveys')}>Back</Button>}
      />

      <SectionCard title="Details">
        <Stack spacing={2}>
          <TextField label="Title" value={meta.title} onChange={(e) => setMetaField('title', e.target.value)} fullWidth required />
          <TextField label="Description" value={meta.description} onChange={(e) => setMetaField('description', e.target.value)} fullWidth multiline minRows={2} />
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <TextField type="date" label="Start date" value={meta.start_date} onChange={(e) => setMetaField('start_date', e.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField type="date" label="End date" value={meta.end_date} onChange={(e) => setMetaField('end_date', e.target.value)} InputLabelProps={{ shrink: true }} />
          </Stack>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <FormControlLabel control={<Checkbox checked={meta.is_anonymous} onChange={(e) => setMetaField('is_anonymous', e.target.checked)} />} label="Confidential (hide respondent identity)" />
            <FormControlLabel control={<Checkbox checked={meta.allow_multiple_submissions} onChange={(e) => setMetaField('allow_multiple_submissions', e.target.checked)} />} label="Allow multiple submissions" />
          </Stack>

          <Divider />
          <Typography variant="subtitle2">Audience</Typography>
          {meta.audiences.map((a, i) => (
            <Stack key={i} direction="row" spacing={1} alignItems="center">
              <TextField
                select size="small" label="Type" value={a.audience_type} sx={{ minWidth: 200 }}
                onChange={(e) => setMeta((prev) => ({ ...prev, audiences: prev.audiences.map((x, xi) => (xi === i ? { ...x, audience_type: e.target.value } : x)) }))}
              >
                {AUDIENCE_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </TextField>
              {a.audience_type !== 'all' && (
                <TextField
                  size="small" label="Value" value={a.audience_value}
                  onChange={(e) => setMeta((prev) => ({ ...prev, audiences: prev.audiences.map((x, xi) => (xi === i ? { ...x, audience_value: e.target.value } : x)) }))}
                />
              )}
              <IconButton size="small" onClick={() => setMeta((prev) => ({ ...prev, audiences: prev.audiences.filter((_, xi) => xi !== i) }))}>✕</IconButton>
            </Stack>
          ))}
          <Box>
            <Button size="small" onClick={() => setMeta((prev) => ({ ...prev, audiences: [...prev.audiences, { audience_type: 'department', audience_value: '' }] }))}>
              + Add audience rule
            </Button>
          </Box>

          <Box>
            <Button variant="contained" onClick={saveMeta} disabled={savingMeta || !meta.title.trim()}>
              {editing ? 'Save details' : 'Create survey'}
            </Button>
          </Box>
        </Stack>
      </SectionCard>

      {editing && (
        <>
          <SectionCard title="Questions" action={<Button variant="outlined" onClick={addQuestion}>Add question</Button>}>
            {questions.length === 0 ? (
              <EmptyState title="No questions yet" description="Add your first question." />
            ) : (
              <Stack spacing={2}>
                {questions.map((q, idx) => (
                  <QuestionEditor
                    key={q.id}
                    q={q}
                    index={idx}
                    earlier={questions.slice(0, idx)}
                    onPatch={(patch) => patchQuestion(q.id, patch)}
                    onSave={() => saveQuestion(questions.find((x) => x.id === q.id))}
                    onRemove={() => removeQuestion(q.id)}
                    onMoveUp={() => move(idx, -1)}
                    onMoveDown={() => move(idx, 1)}
                  />
                ))}
              </Stack>
            )}
          </SectionCard>

          <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button variant="contained" color="success" onClick={validateAndActivate}>Validate &amp; Activate</Button>
          </Stack>
        </>
      )}
    </PageLayout>
  );
}

function QuestionEditor({ q, index, earlier, onPatch, onSave, onRemove, onMoveUp, onMoveDown }) {
  const hasOptions = usesOptions(q.type);

  const setOption = (i, patch) => onPatch({ options: q.options.map((o, oi) => (oi === i ? { ...o, ...patch } : o)) });
  const addOption = () => onPatch({ options: [...q.options, { value: '', label: '', is_exclusive: false }] });
  const removeOption = (i) => onPatch({ options: q.options.filter((_, oi) => oi !== i) });

  const rule = q.rule || { logic: 'AND', conditions: [] };
  const setRule = (patch) => onPatch({ rule: { ...rule, ...patch } });
  const addCondition = () => setRule({ conditions: [...rule.conditions, { source_question_id: '', operator: 'equals', value: '' }] });
  const setCondition = (i, patch) => setRule({ conditions: rule.conditions.map((c, ci) => (ci === i ? { ...c, ...patch } : c)) });
  const removeCondition = (i) => {
    const next = rule.conditions.filter((_, ci) => ci !== i);
    onPatch({ rule: next.length ? { ...rule, conditions: next } : null });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Question {index + 1}</Typography>
        <Stack direction="row" spacing={0.5}>
          <IconButton size="small" onClick={onMoveUp}>↑</IconButton>
          <IconButton size="small" onClick={onMoveDown}>↓</IconButton>
          <Button size="small" color="error" onClick={onRemove}>Remove</Button>
        </Stack>
      </Stack>

      <Stack spacing={1.5}>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <TextField
            select size="small" label="Type" value={q.type} sx={{ minWidth: 180 }}
            onChange={(e) => onPatch({ type: e.target.value })}
          >
            {QUESTION_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
          <FormControlLabel
            control={<Checkbox checked={q.is_required} onChange={(e) => onPatch({ is_required: e.target.checked })} />}
            label="Required"
          />
        </Stack>

        <TextField label="Question text" value={q.label} onChange={(e) => onPatch({ label: e.target.value })} fullWidth />
        <TextField label="Help text (optional)" value={q.help_text} onChange={(e) => onPatch({ help_text: e.target.value })} fullWidth size="small" />

        {q.type === 'rating' && (
          <Stack direction="row" spacing={2}>
            <TextField
              size="small" type="number" label="Min" value={q.settings.min ?? 1}
              onChange={(e) => onPatch({ settings: { ...q.settings, min: Number(e.target.value) } })}
            />
            <TextField
              size="small" type="number" label="Max" value={q.settings.max ?? 5}
              onChange={(e) => onPatch({ settings: { ...q.settings, max: Number(e.target.value) } })}
            />
          </Stack>
        )}

        {hasOptions && (
          <Box>
            <Typography variant="caption" color="text.secondary">Options</Typography>
            {q.options.map((o, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <TextField size="small" label="Value" value={o.value} onChange={(e) => setOption(i, { value: e.target.value })} />
                <TextField size="small" label="Label" value={o.label} onChange={(e) => setOption(i, { label: e.target.value })} />
                <FormControlLabel
                  control={<Checkbox size="small" checked={!!o.is_exclusive} onChange={(e) => setOption(i, { is_exclusive: e.target.checked })} />}
                  label="Exclusive"
                />
                <IconButton size="small" onClick={() => removeOption(i)}>✕</IconButton>
              </Stack>
            ))}
            <Button size="small" onClick={addOption} sx={{ mt: 0.5 }}>+ Add option</Button>
          </Box>
        )}

        {index > 0 && (
          <Box sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Show this question only when {rule.conditions.length > 1 ? `(${rule.logic})` : ''}:
            </Typography>
            {rule.conditions.map((c, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
                <TextField
                  select size="small" label="Question" value={c.source_question_id} sx={{ minWidth: 160 }}
                  onChange={(e) => setCondition(i, { source_question_id: e.target.value })}
                >
                  {earlier.map((eq, ei) => <MenuItem key={eq.id} value={eq.id}>{`Q${ei + 1}: ${eq.label.slice(0, 24)}`}</MenuItem>)}
                </TextField>
                <TextField
                  select size="small" label="Operator" value={c.operator} sx={{ minWidth: 140 }}
                  onChange={(e) => setCondition(i, { operator: e.target.value })}
                >
                  {CONDITION_OPERATORS.map((op) => <MenuItem key={op.value} value={op.value}>{op.label}</MenuItem>)}
                </TextField>
                {operatorNeedsValue(c.operator) && (
                  <TextField
                    size="small" label="Value" value={c.value}
                    helperText={['in', 'not_in', 'between'].includes(c.operator) ? 'comma-separated' : undefined}
                    onChange={(e) => setCondition(i, { value: e.target.value })}
                  />
                )}
                <IconButton size="small" onClick={() => removeCondition(i)}>✕</IconButton>
              </Stack>
            ))}
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Button size="small" onClick={addCondition}>+ Add condition</Button>
              {rule.conditions.length > 1 && (
                <TextField
                  select size="small" label="Match" value={rule.logic} sx={{ minWidth: 100 }}
                  onChange={(e) => setRule({ logic: e.target.value })}
                >
                  <MenuItem value="AND">All</MenuItem>
                  <MenuItem value="OR">Any</MenuItem>
                </TextField>
              )}
            </Stack>
          </Box>
        )}

        <Box>
          <Button variant="contained" size="small" onClick={onSave}>Save question</Button>
        </Box>
      </Stack>
    </Paper>
  );
}
